import isEqual from 'lodash/isEqual';

import type { UiOrganization } from '../../schemas/organizations.schema';
import type { Maybe } from '../../types/maybe.types';

/**
 * The fields the stored organization shares with a switcher list row. Everything
 * else on the snapshot (contacts, addresses, …) comes from richer sources —
 * `/auth/me`'s `lastUsedOrganization` or `/set-preferred-organization` — and a
 * list row must never be allowed to drop it.
 */
const RECONCILED_FIELDS = [
  // §16 (R-AUD-62): computed per row by `me/organizations`, and — like
  // `rebrandActive` — absent from the raw `/set-preferred-organization`
  // response `selectOrganization` stores. Reconciling it is what lets the
  // billing banner explain the lock to a member who cannot read billing, and
  // it keeps the flag LIVE: a lock that lands or drops while the tab is open
  // arrives with the next list fetch.
  'billingLocked',
  // Q3/Task 4: WHY the organization is locked travels with the flag and must be
  // reconciled with it. Left out, a person switching from an UNPAID
  // organization into a CANCELLED one would keep the previous organization's
  // reason — which is how a paid-up organization gets labelled a debtor by a
  // stale field rather than by wrong copy.
  'billingLockReason',
  'defaultCurrency',
  // The super admin's Desk / Keys switches: the sidebar and both page gates
  // read them off this snapshot, so a save would otherwise show only after a
  // reload (a switched-off module still listed, its routes answering 403).
  'deskEnabled',
  'lockerKeysEnabled',
  'logoName',
  'logoUrl',
  'name',
  'rebrandActive',
  'theme',
  // Foundation 1 §5.2 — the organization zone `organizationTimeZoneFor`
  // reads off `currentOrganization`; without reconciling it, an owner who
  // changes the zone in settings keeps computing "today" in the old one
  // until a hard refresh.
  'timezone',
] as const;

/**
 * Refreshes the selected organization's display/branding fields from the live
 * organization list.
 *
 * `currentOrganization` is a state SNAPSHOT: it is seeded once and the seeding
 * effect bails out for as long as an organization is selected. That is
 * deliberate — the list is search-filtered and paginated, so the active org can
 * drop out of it and the snapshot is what keeps the theme from reverting. But
 * it also meant the snapshot never took an UPDATE either: changing the org logo
 * refreshed the org page (its own query is invalidated) while the sidebar
 * switcher trigger and the rebrand logo kept showing the old picture until a
 * hard refresh.
 *
 * Returns the SAME object when nothing changed — the caller writes the result
 * straight back into state, so a fresh object per list fetch would re-render
 * forever.
 */
export function reconcileCurrentOrganization(
  current: Maybe<UiOrganization>,
  rows: Maybe<Maybe<UiOrganization>[]>,
): Maybe<UiOrganization> {
  if (!current?.id) return current;

  const fresh = rows?.find((row) => row?.id === current.id);
  if (!fresh) return current;

  // `undefined` is ABSENT, not cleared: no single endpoint fills every field
  // (`/auth/me` signs the logo into `logoName`, the switcher rows fill
  // `logoUrl`, `rebrandActive` is computed only on some), so adopting an
  // absent one would blank a value the snapshot legitimately holds. An
  // explicit `null` — the user removed the logo — still comes through.
  const changed = RECONCILED_FIELDS.filter(
    (field) =>
      fresh[field] !== undefined && !isEqual(current[field], fresh[field]),
  );
  if (!changed.length) return current;

  return changed.reduce<UiOrganization>(
    (organization, field) => ({ ...organization, [field]: fresh[field] }),
    current,
  );
}
